import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import { useParams } from "react-router-dom";
import { CURRENCY_DICT } from "utils/invoice";
import { ODOO_ACCOUNTING_FORECAST } from "./graphql";
import InvBillView from "./inside/InvBillView";
import OrdersView from "./inside/OrdersView";
import PaymentsView from "./inside/PaymentsView";
import RecurringItemsView from "./inside/RecurringItemsView";
import OverviewChart from "./OverviewChart";

const CompanyDetail = () => {
  const { company } = useParams();
  const { loading, error, data, refetch } = useQuery(ODOO_ACCOUNTING_FORECAST, {
    variables: { company: company.toUpperCase(), refresh: false },
    notifyOnNetworkStatusChange: true,
  });
  const modal = useModals();

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const res = JSON.parse(data.odooAccountingForecast);

  console.log("res", res);

  const currency = company.toUpperCase() === "LTD" ? "CNY" : "SEK";
  const currencySymbol = CURRENCY_DICT[currency].symbol;

  function showDetail(title, date, data) {
    if (!data || data.length === 0) {
      window.alert("No data available.");
      return;
    }

    if (title.includes("Payments")) {
      modal.present({
        title,
        subtitle: `Due Date: ${date}`,
        maxWidth: "max-w-full",
        children: <PaymentsView data={data} currencySymbol={currencySymbol} />,
      });
    } else if (title.includes("Orders")) {
      modal.present({
        title,
        subtitle: `Due Date: ${date}`,
        maxWidth: "max-w-full",
        children: <OrdersView data={data} currencySymbol={currencySymbol} />,
      });
    } else if (title.includes("RECUR")) {
      modal.present({
        title,
        subtitle: `Due Date: ${date}`,
        maxWidth: "max-w-full",
        children: (
          <RecurringItemsView data={data} currencySymbol={currencySymbol} />
        ),
      });
    } else {
      modal.present({
        title,
        subtitle: `Due Date: ${date}`,
        maxWidth: "max-w-full",
        children: <InvBillView data={data} currencySymbol={currencySymbol} />,
      });
    }
  }

  const todayRow = res.find(
    (item) => item.due_date === new Date().toISOString().split("T")[0],
  );

  const todayString = new Date().toISOString().split("T")[0];

  const overdueARTotal = res
    .filter(
      (item) => item.due_date <= todayString && item.invoice_amount_due > 0,
    )
    .reduce((acc, item) => acc + item.invoice_amount_due, 0);

  const overdueAPTotal = res
    .filter(
      (item) => item.due_date <= todayString && item.bill_amount_due !== 0,
    )
    .reduce((acc, item) => acc + item.bill_amount_due, 0);
  const forecastARTotal = res
    .filter(
      (item) => item.due_date > todayString && item.invoice_amount_due > 0,
    )
    .reduce((acc, item) => acc + item.invoice_amount_due, 0);
  const forecastAPTotal = res
    .filter((item) => item.due_date > todayString && item.bill_amount_due !== 0)
    .reduce((acc, item) => acc + item.bill_amount_due, 0);

  const unIninvoicedSoTotal = res.reduce(
    (acc, item) => acc + item.so_uninvoiced,
    0,
  );
  const unBilledPoTotal = res.reduce(
    (acc, item) => acc + item.po_uninvoiced,
    0,
  );

  const hasRecurringData = res.filter((i) => i.recurring_data !== 0).length > 0;

  return (
    <Page
      title={`Overview - ${company.toUpperCase()}(${currency})`}
      className="h-screen"
      backTo="./.."
      rightButtons={
        <div className="flex items-center space-x-4">
          <Button
            loading={loading}
            onClick={() =>
              refetch({ company: company.toUpperCase(), refresh: true })
            }
          >
            Refresh Data
          </Button>
        </div>
      }
    >
      <div className="p-6 grid grid-cols-12 gap-6">
        <div className="col-span-8 row-span-3">
          <div className="card h-full">
            <OverviewChart data={res} />
          </div>
        </div>

        <div className="col-span-4">
          <div className="card h-full">
            <h5>Today</h5>
            <div className="mt-2 font-code -mx-2 text-base">
              <table>
                <tbody>
                  <tr>
                    <td className="py-1">
                      <label>Balance:</label>
                    </td>
                    <td className="py-1 text-right">
                      {todayRow.payment_balance_cumsum.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {currencySymbol}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1">
                      <label>Overdue AR:</label>
                    </td>
                    <td className="py-1 text-right">
                      {overdueARTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {currencySymbol}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1">
                      <label>Overdue AP:</label>
                    </td>
                    <td className="py-1 text-right">
                      {overdueAPTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {currencySymbol}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-span-4">
          <div className="card h-full">
            <h5>Forecast</h5>
            <div className="mt-2 font-code -mx-2 text-base">
              <table>
                <tbody>
                  <tr>
                    <td className="py-1">
                      <label>Unpaid Invoices:</label>
                    </td>
                    <td className="py-1 text-right">
                      {forecastARTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {currencySymbol}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1">
                      <label>Unpaid Bills:</label>
                    </td>
                    <td className="py-1 text-right">
                      {forecastAPTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {currencySymbol}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-span-4">
          <div className="card h-full">
            <h5>No INV/BILL</h5>
            <div className="mt-2 font-code -mx-2 text-base">
              <table>
                <tbody>
                  <tr>
                    <td className="py-1">
                      <label>Uninvoiced SO:</label>
                    </td>
                    <td className="py-1 text-right">
                      {unIninvoicedSoTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {currencySymbol}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1">
                      <label>Unbilled PO:</label>
                    </td>
                    <td className="py-1 text-right">
                      {unBilledPoTotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {currencySymbol}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-span-12 space-y-6">
          <div className="card">
            <h5>Payments, Invoices, Bills, SO, PO</h5>
            <div className="mt-1 opacity-70">
              <p>The date for payments are the payment date.</p>
              <p>The date for invoices and bill are the due date.</p>
              <p>
                The date for SO is the estimated date base on expected delivery
                date + the payment terms.
              </p>
              <p>
                The date for PO is the expected delivery date. Note that all the
                unbilled PO in the past will be sum up to the next date so that
                it will not be lost.
              </p>
              <p>
                Note that for Sales and Purchase Orders, the converted amount is
                based on the currency rate in last 3 days.
              </p>
              <p>
                Colors: light yellow for overdue, light pink for negative final
                forecast, green for today.
              </p>
            </div>
            <div className="mt-2 -mx-8 font-code whitespace-nowrap">
              <table>
                <thead>
                  <tr className="sticky top-0 bg-gray-50 bg-opacity-70 backdrop-blur">
                    <th className="w-full px-8">Date</th>
                    <th className="text-right">Income</th>
                    <th className="text-right">Outgoing</th>
                    <th className="text-right">Payment Balance</th>
                    <th className="text-right">Unpaid Invoices(AR)</th>
                    <th className="text-right">Unpaid Bills(AP)</th>
                    {hasRecurringData && (
                      <th className="text-right">Recur Items</th>
                    )}

                    <th className="text-right">Fcst + AR/AP + Recur</th>
                    <th className="text-right">SO(Uninvoiced)</th>
                    <th className="text-right">PO(Unbilled)</th>
                    <th className="text-right px-8">Final Fcst</th>
                  </tr>
                </thead>
                <tbody>
                  {res.map((i, index) => (
                    <tr
                      key={index}
                      className={`border-y dark:border-gray-700 dark:bg-opacity-30
                      ${
                        i.due_date === todayString
                          ? "bg-emerald-200 dark:bg-emerald-900"
                          : i.due_date > new Date().toISOString().split("T")[0]
                            ? i.forecast_balance_cumsum < 0
                              ? "bg-pink-200"
                              : ""
                            : i.invoice_amount_due > 0 || i.bill_amount_due < 0
                              ? i.due_date <=
                                new Date().toISOString().split("T")[0]
                                ? "bg-yellow-200 dark:bg-yellow-900"
                                : "bg-teal-200 dark:bg-teal-900"
                              : ""
                      }
                      `}
                    >
                      <td className="px-8">{i.due_date}</td>
                      <td className="text-right">
                        <button
                          className="font-semibold text-sky-600 hover:text-sky-700 active:text-sky-800"
                          onClick={() =>
                            showDetail("Payments", i.due_date, i.income_data)
                          }
                        >
                          {i.income_amount.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </button>
                      </td>
                      <td className="text-right">
                        <button
                          className="font-semibold text-sky-600 hover:text-sky-700 active:text-sky-800"
                          onClick={() =>
                            showDetail("Payments", i.due_date, i.outcome_data)
                          }
                        >
                          {i.outcome_amount.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </button>
                      </td>
                      <td className="text-right">
                        {i.payment_balance_cumsum.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>

                      <td className="text-right">
                        <button
                          className="font-semibold text-sky-600 hover:text-sky-700 active:text-sky-800"
                          onClick={() =>
                            showDetail("Invoices", i.due_date, i.invoice_data)
                          }
                        >
                          {i.invoice_amount_due.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </button>
                      </td>
                      <td className="text-right">
                        <button
                          className="font-semibold text-sky-600 hover:text-sky-700 active:text-sky-800"
                          onClick={() =>
                            showDetail("Bills", i.due_date, i.bill_data)
                          }
                        >
                          {i.bill_amount_due.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </button>
                      </td>
                      {hasRecurringData && (
                        <td className="text-right">
                          <button
                            className="font-semibold text-sky-600 hover:text-sky-700 active:text-sky-800"
                            onClick={() =>
                              showDetail("RECUR", i.due_date, i.recurring_data)
                            }
                          >
                            {i.recurring_amount.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </button>
                        </td>
                      )}

                      <td className="text-right">
                        {i.payment_invoice_recurring_balance_cumsum.toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </td>

                      <td className="text-right">
                        <button
                          className="font-semibold text-sky-600 hover:text-sky-700 active:text-sky-800"
                          onClick={() =>
                            showDetail("Sales Orders", i.due_date, i.so_data)
                          }
                        >
                          {i.so_uninvoiced.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </button>
                      </td>
                      <td className="text-right">
                        <button
                          className="font-semibold text-sky-600 hover:text-sky-700 active:text-sky-800"
                          onClick={() =>
                            showDetail("Purchase Orders", i.due_date, i.po_data)
                          }
                        >
                          {i.po_uninvoiced.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </button>
                      </td>

                      <td className="text-right px-8">
                        {i.forecast_balance_cumsum.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default CompanyDetail;
